<script setup lang="ts">
import { SolButton } from '@solfacil/girassol'
import { useI18n } from 'vue-i18n'
import IconChevronLeft from '~icons/material-symbols/chevron-left'
import IconChevronRight from '~icons/material-symbols/chevron-right'

export interface NewsItem {
  id: number
  image: string
  title: string
  description?: string
  buttonText: string
  action?: (() => void) | string
  alt?: string
}

interface Props {
  news?: NewsItem[]
  autoPlayInterval?: number
  title?: string
}

const props = withDefaults(defineProps<Props>(), {
  news: () => [],
  autoPlayInterval: 0,
})

const { t } = useI18n()

const currentIndex = ref(0)
const scrollContainer = ref<HTMLElement | null>(null)
const direction = ref<'next' | 'prev'>('next')
const leavingItemId = ref<number | string | null>(null)
let autoPlayTimer: ReturnType<typeof setInterval> | null = null

const hasNews = computed(() => props.news.length > 0)

const { screenWidth } = useScreenSize()
const isDesktop = computed(() => screenWidth.value >= 1024)

const itemsVisible = computed(() => isDesktop.value ? 3 : 1)
const maxIndex = computed(() => Math.max(0, props.news.length - itemsVisible.value))
const canGoNext = computed(() => currentIndex.value < maxIndex.value)
const canGoPrev = computed(() => currentIndex.value > 0)
const shouldShowButtons = computed(() => props.news.length > 3)

const visibleNews = computed(() => {
  if (!isDesktop.value) {
    return props.news
  }
  const start = currentIndex.value
  const end = start + itemsVisible.value
  const items = props.news.slice(start, end)

  if (leavingItemId.value !== null) {
    const leavingItem = props.news.find(item => item.id === leavingItemId.value)
    if (leavingItem && !items.find(item => item.id === leavingItem.id)) {
      items.push(leavingItem)
    }
  }

  return items
})

function nextNews() {
  if (props.news.length === 0)
    return

  if (isDesktop.value) {
    direction.value = 'next'
    const nextIndex = Math.min(currentIndex.value + 1, maxIndex.value)
    currentIndex.value = nextIndex
  }
  else {
    scrollToIndex(currentIndex.value + 1)
  }
}

function previousNews() {
  if (props.news.length === 0)
    return

  if (isDesktop.value) {
    direction.value = 'prev'

    const leavingIndex = currentIndex.value + itemsVisible.value - 1
    if (leavingIndex < props.news.length) {
      leavingItemId.value = props.news[leavingIndex].id
    }

    const prevIndex = Math.max(0, currentIndex.value - 1)
    currentIndex.value = prevIndex

    setTimeout(() => {
      leavingItemId.value = null
    }, 400)
  }
  else {
    scrollToIndex(currentIndex.value - 1)
  }
}

function scrollToIndex(index: number) {
  if (!scrollContainer.value)
    return

  const clampedIndex = Math.max(0, Math.min(index, props.news.length - 1))
  currentIndex.value = clampedIndex

  const item = scrollContainer.value.children[clampedIndex] as HTMLElement
  if (item) {
    item.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    })
  }
}

function handleNewsAction(newsItem: NewsItem) {
  if (newsItem.action) {
    if (typeof newsItem.action === 'function') {
      newsItem.action()
    }
    else if (typeof newsItem.action === 'string') {
      window.open(newsItem.action, '_blank')
    }
  }
}

function formatDescription(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/<strong>(.*?)<\/strong>/g, '<strong>$1</strong>')
}

function startAutoPlay() {
  if (props.autoPlayInterval <= 0 || props.news.length <= 1)
    return

  autoPlayTimer = setInterval(() => {
    nextNews()
  }, props.autoPlayInterval)
}

function stopAutoPlay() {
  if (autoPlayTimer) {
    clearInterval(autoPlayTimer)
    autoPlayTimer = null
  }
}

function resetAutoPlay() {
  stopAutoPlay()
  startAutoPlay()
}

watch(() => props.news, () => {
  if (props.news.length > 0 && currentIndex.value >= props.news.length) {
    currentIndex.value = 0
  }
  resetAutoPlay()
}, { immediate: true })

onMounted(() => {
  startAutoPlay()
})

onUnmounted(() => {
  stopAutoPlay()
})
</script>

<template>
  <div
    v-if="hasNews"
    class="news-carousel"
  >
    <div
      v-if="isDesktop"
      class="news-carousel-desktop"
      :class="{ 'news-carousel-desktop-no-buttons': !shouldShowButtons }"
    >
      <button
        v-if="shouldShowButtons"
        class="news-carousel-button news-carousel-button-prev"
        :disabled="!canGoPrev"
        @click="previousNews"
      >
        <IconChevronLeft class="news-carousel-button-icon" />
      </button>

      <div class="news-carousel-content-wrapper">
        <h2 class="news-carousel-header-title">
          {{ t('overview.news.title') }}
        </h2>
        <div
          ref="scrollContainer"
          class="news-carousel-scroll"
        >
          <TransitionGroup
            :name="direction === 'next' ? 'news-item' : 'news-item-reverse'"
            tag="div"
            class="news-carousel-items-group"
            :move-class="direction === 'next' ? 'news-item-move' : 'news-item-reverse-move'"
          >
            <div
              v-for="(newsItem, index) in visibleNews"
              :key="newsItem.id"
              class="news-carousel-item"
              :data-index="currentIndex + index"
            >
              <div class="news-carousel-image-wrapper">
                <img
                  :src="newsItem.image"
                  :alt="newsItem.alt || newsItem.title"
                  class="news-carousel-image"
                >
              </div>
              <p
                v-if="newsItem.description"
                class="news-carousel-description"
                v-html="formatDescription(newsItem.description)"
              />
              <SolButton
                v-if="newsItem.buttonText"
                :id="`news-carousel-action-${currentIndex + index}`"
                size="small"
                variant="secondary"
                @click="handleNewsAction(newsItem)"
              >
                {{ newsItem.buttonText }}
              </SolButton>
            </div>
          </TransitionGroup>
        </div>
      </div>

      <button
        v-if="shouldShowButtons"
        class="news-carousel-button news-carousel-button-next"
        :disabled="!canGoNext"
        @click="nextNews"
      >
        <IconChevronRight class="news-carousel-button-icon" />
      </button>
    </div>

    <div
      v-else
      class="news-carousel-mobile"
    >
      <div class="news-carousel-mobile-wrapper">
        <h2 class="news-carousel-mobile-title">
          {{ t('overview.news.title') }}
        </h2>
        <div
          ref="scrollContainer"
          class="news-carousel-scroll-mobile"
        >
          <div
            v-for="(newsItem, index) in news"
            :key="newsItem.id"
            class="news-carousel-item-mobile"
          >
            <div class="news-carousel-image-wrapper-mobile">
              <img
                :src="newsItem.image"
                :alt="newsItem.alt || newsItem.title"
                class="news-carousel-image-mobile"
              >
            </div>
            <div class="news-carousel-content-mobile">
              <p
                v-if="newsItem.description"
                class="news-carousel-description-mobile"
                v-html="formatDescription(newsItem.description)"
              />
              <SolButton
                v-if="newsItem.buttonText"
                :id="`news-carousel-action-mobile-${index}`"
                size="small"
                variant="secondary"
                @click="handleNewsAction(newsItem)"
              >
                {{ newsItem.buttonText }}
              </SolButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.news-carousel {
  @apply flex flex-col w-full;
  @apply self-stretch;

  &-desktop {
    @apply flex items-start gap-6;
    @apply bg-neutral-high-pure border border-neutral-high-medium rounded-2xl;
    @apply w-full;
    @apply overflow-hidden;

    &.news-carousel-desktop-no-buttons {
      @apply px-8;
    }
  }

  &-content-wrapper {
    @apply flex flex-col gap-6;
    @apply flex-1;
    @apply min-w-0;
    @apply py-6;
  }

  &-header-title {
    @apply fonts-heading-h4 text-neutral-low-dark;
    @apply px-0;
    @apply whitespace-nowrap;
  }

  &-scroll {
    @apply overflow-hidden;
    @apply pb-0;
    @apply pt-0;
    @apply px-0;

    @screen xs:system {
      @apply flex gap-6 overflow-x-auto;
      scroll-snap-type: x mandatory;
      -webkit-overflow-scrolling: touch;
      scrollbar-width: none;
      scroll-behavior: smooth;

      &::-webkit-scrollbar {
        display: none;
      }
    }
  }

  &-items-group {
    @apply flex gap-6 w-full items-stretch  min-w-0 relative;
  }

  &-item {
    @apply flex flex-col gap-4 flex-shrink-0 flex-1  min-w-0;
    will-change: transform, opacity;

    @screen xs:system {
      scroll-snap-align: start;
    }

    @screen md:system {
      flex: 0 0 calc(33.333% - 16px);
      max-width: calc(33.333% - 16px);
    }
  }

  &-image-wrapper {
    @apply bg-brand-primary-light;
    @apply flex items-center justify-center;
    @apply rounded-sm h-[104px] overflow-hidden relative w-full;
  }

  &-image {
    @apply w-full h-full block;
    object-fit: cover;
    object-position: center;
  }

  &-content {
    @apply flex flex-col gap-4 items-start w-full min-h-0;
  }

  &-description {
    @apply fonts-body-medium-regular text-neutral-low-dark text-left w-full;

    :deep(strong) {
      @apply fonts-body-medium-bold;
    }
  }

  &-button {
    @apply flex-shrink-0 w-8 h-full flex items-center justify-center cursor-pointer;
    @apply transition-all duration-200 hover:bg-neutral-high-light p-1 self-stretch;

    &-prev {
      @apply border-r-2 border-neutral-high-medium;
    }

    &-next {
      @apply border-l-2 border-neutral-high-medium;
    }

    &-icon {
      @apply w-6 h-6;
      @apply text-neutral-low-dark;
    }

    &:disabled {
      @apply opacity-50 cursor-not-allowed;
      @apply hover:bg-neutral-high-pure;
    }
  }

  &-mobile {
    @apply w-full;
  }

  &-mobile-wrapper {
    @apply bg-neutral-high-pure border border-neutral-high-medium rounded-2xl;
    @apply flex flex-col gap-6;
    @apply p-6;
    @apply w-full;
    @apply overflow-visible;
  }

  &-mobile-title {
    @apply fonts-heading-h4 text-neutral-low-dark;
  }

  &-scroll-mobile {
    @apply flex overflow-x-auto;
    @apply pb-2;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
    gap: 24px;
    @apply items-stretch;

    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }

    @screen sm:system {
      scrollbar-width: thin;
      scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

      &::-webkit-scrollbar {
        height: 8px;
        display: block;
      }

      &::-webkit-scrollbar-track {
        background: transparent;
      }

      &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.2);
        border-radius: 4px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background: rgba(0, 0, 0, 0.3);
      }
    }
  }

  &-item-mobile {
    @apply flex flex-col gap-4;
    @apply flex-shrink-0 w-[208px];
    scroll-snap-align: start;

    @screen sm:system {
      @apply w-[264px] min-w-[264px];
    }
  }

  &-image-wrapper-mobile {
    @apply bg-brand-primary-light;
    @apply flex items-center justify-center;
    @apply rounded-sm bg-brand-secondary-pure h-[104px] overflow-hidden w-full;
  }

  &-image-mobile {
    @apply w-full h-full block;
    object-fit: cover;
  }

  &-content-mobile {
    @apply flex flex-col gap-4 items-start;
    @apply w-full min-h-0 flex-1 justify-between;
  }

  &-description-mobile {
    @apply fonts-body-medium-regular text-neutral-low-dark;
    @apply text-left w-full;

    :deep(strong) {
      @apply fonts-body-medium-bold;
    }
  }
}

.news-item-enter-active {
  transition: all 0.4s ease-in-out;
}

.news-item-leave-active {
  transition: all 0.4s ease-in-out;
  position: absolute;
  width: calc(33.333% - 16px);
  top: 0;
  left: 0;
  right: auto;
  bottom: auto;
}

.news-item-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.news-item-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.news-item-enter-to,
.news-item-leave-from {
  transform: translateX(0);
  opacity: 1;
}

.news-item-move {
  transition: transform 0.4s ease-in-out;
}

.news-item-reverse-enter-active {
  transition: all 0.4s ease-in-out;
}

.news-item-reverse-leave-active {
  transition: all 0.4s ease-in-out;
  position: absolute;
  width: calc(33.333% - 16px);
  top: 0;
  left: auto;
  right: 0;
  bottom: auto;
}

.news-item-reverse-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}

.news-item-reverse-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.news-item-reverse-enter-to,
.news-item-reverse-leave-from {
  transform: translateX(0);
  opacity: 1;
}

.news-item-reverse-move {
  transition: transform 0.4s ease-in-out;
}
</style>
